import type { CSSProperties } from 'react'
import { BaseEdge, type EdgeProps, getSmoothStepPath } from '@xyflow/react'

import type { StateMachineEdgeData } from '../../ProgramStateMachine.types'

const EDGE_OFFSET = 20
const EDGE_BORDER_RADIUS = 8

interface Point {
  x: number
  y: number
}

/**
 * Build an SVG path through orthogonal points with rounded corners.
 */
function buildOrthogonalPath(points: Point[], radius: number): string {
  if (points.length < 2) return ''

  let d = `M ${points[0].x} ${points[0].y}`

  for (let i = 1; i < points.length - 1; i += 1) {
    const prev = points[i - 1]
    const curr = points[i]
    const next = points[i + 1]

    const approachDx = curr.x - prev.x
    const approachDy = curr.y - prev.y
    const departDx = next.x - curr.x
    const departDy = next.y - curr.y

    const approachDist = Math.max(Math.abs(approachDx), Math.abs(approachDy))
    const departDist = Math.max(Math.abs(departDx), Math.abs(departDy))
    const r = Math.min(radius, approachDist / 2, departDist / 2)

    if (r <= 0) {
      d += ` L ${curr.x} ${curr.y}`
    } else {
      const adx = approachDx !== 0 ? Math.sign(approachDx) : 0
      const ady = approachDy !== 0 ? Math.sign(approachDy) : 0
      const ddx = departDx !== 0 ? Math.sign(departDx) : 0
      const ddy = departDy !== 0 ? Math.sign(departDy) : 0

      d += ` L ${curr.x - adx * r} ${curr.y - ady * r}`
      d += ` Q ${curr.x} ${curr.y} ${curr.x + ddx * r} ${curr.y + ddy * r}`
    }
  }

  const last = points[points.length - 1]
  d += ` L ${last.x} ${last.y}`

  return d
}

export function StateMachineEdge({
  sourceX,
  sourceY,
  sourcePosition,
  targetX,
  targetY,
  targetPosition,
  markerEnd,
  style: rawStyle,
  data: rawData
}: EdgeProps) {
  const edgeData = rawData as StateMachineEdgeData | undefined
  const style = rawStyle as CSSProperties | undefined

  if (edgeData?.crossMode !== undefined) {
    const points: Point[] = [{ x: sourceX, y: sourceY }]

    if (edgeData.crossMode === 'top') {
      // Horizontal near source, long vertical at targetX
      points.push({ x: sourceX, y: sourceY + EDGE_OFFSET })
      points.push({ x: targetX, y: sourceY + EDGE_OFFSET })
    } else {
      // Long vertical at sourceX, horizontal near target
      points.push({ x: sourceX, y: targetY - EDGE_OFFSET })
      points.push({ x: targetX, y: targetY - EDGE_OFFSET })
    }

    points.push({ x: targetX, y: targetY })

    const edgePath = buildOrthogonalPath(points, EDGE_BORDER_RADIUS)

    return <BaseEdge path={edgePath} markerEnd={markerEnd} style={style} />
  }

  if (edgeData?.detourX !== undefined) {
    const points: Point[] = [
      { x: sourceX, y: sourceY },
      { x: sourceX, y: sourceY + EDGE_OFFSET },
      { x: edgeData.detourX, y: sourceY + EDGE_OFFSET },
      { x: edgeData.detourX, y: targetY - EDGE_OFFSET },
      { x: targetX, y: targetY - EDGE_OFFSET },
      { x: targetX, y: targetY }
    ]

    const edgePath = buildOrthogonalPath(points, EDGE_BORDER_RADIUS)

    return <BaseEdge path={edgePath} markerEnd={markerEnd} style={style} />
  }

  const [edgePath] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    borderRadius: EDGE_BORDER_RADIUS,
    offset: EDGE_OFFSET
  })

  return <BaseEdge path={edgePath} markerEnd={markerEnd} style={style} />
}
